import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

const QUIZ_TYPE_LABELS = {
    single_choice:     'Choix unique',
    multiple_choice:   'Choix multiple',
    matching:          'Association',
    short_text:        'Texte court',
    numeric:           'Numérique',
    fill_in_the_blank: 'Texte à trous',
    ordering:          'Mise en ordre',
};

function successColor(rate) {
    if (rate === null) return 'text-gray-400';
    if (rate < 40) return 'text-red-500 dark:text-red-400';
    if (rate < 70) return 'text-orange-500 dark:text-orange-400';
    return 'text-emerald-500 dark:text-emerald-400';
}

function successBarColor(rate) {
    if (rate === null) return 'bg-gray-300 dark:bg-gray-600';
    if (rate < 40) return 'bg-red-500';
    if (rate < 70) return 'bg-orange-400';
    return 'bg-emerald-500';
}

function RatingBars({ hard, medium, easy, total }) {
    if (!total) return <span className="text-xs text-gray-400">Aucune donnée</span>;
    const pct = (n) => Math.round((n / total) * 100);
    return (
        <div className="space-y-1.5 w-full">
            {[
                { key: 'hard',   n: hard,   bg: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',     label: 'Difficile' },
                { key: 'medium', n: medium, bg: 'bg-orange-400',  text: 'text-orange-600 dark:text-orange-400', label: 'Moyen' },
                { key: 'easy',   n: easy,   bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'Facile' },
            ].map(({ key, n, bg, text, label }) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                    <span className={`w-16 text-right font-medium ${text}`}>{label}</span>
                    <div className="flex-1 bg-gray-100 dark:bg-white/5 rounded-full h-2 overflow-hidden">
                        <div className={`${bg} h-2 rounded-full transition-all`} style={{ width: `${pct(n)}%` }} />
                    </div>
                    <span className="w-16 text-gray-500 dark:text-gray-400">{n} ({pct(n)}%)</span>
                </div>
            ))}
        </div>
    );
}

// ── Barre de difficulté globale ───────────────────────────────────────────────
function DifficultyBar({ quizzes, flashcards }) {
    const allItems = [...quizzes, ...flashcards];
    const hard   = allItems.reduce((s, i) => s + i.hard_count, 0);
    const medium = allItems.reduce((s, i) => s + i.medium_count, 0);
    const easy   = allItems.reduce((s, i) => s + i.easy_count, 0);
    const total  = hard + medium + easy;

    if (!total) {
        return (
            <div className="bg-gray-50 dark:bg-white/5 rounded-2xl px-5 py-4 border border-gray-100 dark:border-white/10">
                <p className="text-xs text-gray-400 text-center">Aucune tentative enregistrée pour cette notion.</p>
            </div>
        );
    }

    const hardPct   = Math.round((hard   / total) * 100);
    const mediumPct = Math.round((medium / total) * 100);
    const easyPct   = Math.round((easy   / total) * 100);

    return (
        <div className="bg-gray-50 dark:bg-white/5 rounded-2xl px-5 py-4 border border-gray-100 dark:border-white/10 space-y-3">
            <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Difficulté globale</p>
                <p className="text-xs text-gray-400">{total.toLocaleString()} tentatives</p>
            </div>
            {/* Stacked bar */}
            <div className="flex h-4 rounded-full overflow-hidden gap-px">
                {hardPct > 0   && <div className="bg-red-500 flex items-center justify-center transition-all" style={{ width: `${hardPct}%` }}>
                    {hardPct >= 10 && <span className="text-[9px] font-bold text-white">{hardPct}%</span>}
                </div>}
                {mediumPct > 0 && <div className="bg-orange-400 flex items-center justify-center transition-all" style={{ width: `${mediumPct}%` }}>
                    {mediumPct >= 10 && <span className="text-[9px] font-bold text-white">{mediumPct}%</span>}
                </div>}
                {easyPct > 0   && <div className="bg-emerald-500 flex items-center justify-center transition-all" style={{ width: `${easyPct}%` }}>
                    {easyPct >= 10 && <span className="text-[9px] font-bold text-white">{easyPct}%</span>}
                </div>}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-red-500 shrink-0" />
                    <span className="text-gray-600 dark:text-gray-400">Difficile <span className="font-bold text-red-500">{hardPct}%</span></span>
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-orange-400 shrink-0" />
                    <span className="text-gray-600 dark:text-gray-400">Moyen <span className="font-bold text-orange-500">{mediumPct}%</span></span>
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 shrink-0" />
                    <span className="text-gray-600 dark:text-gray-400">Facile <span className="font-bold text-emerald-600">{easyPct}%</span></span>
                </span>
            </div>
        </div>
    );
}

// ── QuizTab ───────────────────────────────────────────────────────────────────
function QuizTab({ data }) {
    const [search, setSearch]         = useState('');
    const [sortBy, setSortBy]         = useState('fail_rate');
    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails]       = useState({});
    const [loadingId, setLoadingId]   = useState(null);

    const withAttempts  = data.filter(q => q.total_attempts > 0);
    const avgSuccess    = withAttempts.length
        ? Math.round(withAttempts.reduce((s, q) => s + q.success_rate, 0) / withAttempts.length)
        : null;

    const filtered = data
        .filter(q => q.question.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
            if (sortBy === 'fail_rate') {
                const fa = a.total_attempts ? a.hard_count / a.total_attempts : -1;
                const fb = b.total_attempts ? b.hard_count / b.total_attempts : -1;
                return fb - fa;
            }
            if (sortBy === 'attempts') return b.total_attempts - a.total_attempts;
            return a.question.localeCompare(b.question);
        });

    const loadDetail = useCallback(async (id) => {
        if (details[id]) { setExpandedId(id); return; }
        setLoadingId(id);
        try {
            const res  = await apiClient(`${BASE}/admin_stats.php?action=quiz_detail&id=${id}`);
            const json = await res.json();
            if (json.success) setDetails(d => ({ ...d, [id]: json }));
        } catch {}
        setLoadingId(null);
        setExpandedId(id);
    }, [details]);

    const toggle = (id) => {
        if (expandedId === id) { setExpandedId(null); return; }
        loadDetail(id);
    };

    if (data.length === 0) {
        return <p className="text-center py-10 text-gray-400 text-sm">Aucune question pour cette notion.</p>;
    }

    return (
        <div className="space-y-4">
            {avgSuccess !== null && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Taux de réussite moyen :
                    <span className={`ml-1 font-bold ${successColor(avgSuccess)}`}>{avgSuccess}%</span>
                </p>
            )}
            <div className="flex flex-wrap gap-3 items-center">
                <input
                    value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher une question…"
                    className="flex-1 min-w-[180px] px-3 py-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                />
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none">
                    <option value="fail_rate">Taux d'échec</option>
                    <option value="attempts">Tentatives</option>
                    <option value="alpha">Alphabétique</option>
                </select>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[540px]">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                        <tr>
                            <th className="p-3 text-left">Question</th>
                            <th className="p-3 text-left">Type</th>
                            <th className="p-3 text-center">Tentatives</th>
                            <th className="p-3 text-center">Réussite</th>
                            <th className="p-3 text-center">Tps moy.</th>
                            <th className="p-3 text-center">⚑</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-gray-400">Aucun résultat.</td></tr>
                        )}
                        {filtered.map(q => (
                            <React.Fragment key={q.id}>
                                <tr className={`hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${expandedId === q.id ? 'bg-fuchsia-50 dark:bg-fuchsia-900/10' : ''}`}>
                                    <td className="p-3 max-w-[220px]">
                                        <p className="font-medium text-gray-800 dark:text-gray-200 truncate" title={q.question}>{q.question}</p>
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                                            {QUIZ_TYPE_LABELS[q.type] || q.type}
                                        </span>
                                    </td>
                                    <td className="p-3 text-center font-medium text-gray-700 dark:text-gray-300">
                                        {q.total_attempts || <span className="text-gray-300 dark:text-gray-600">0</span>}
                                    </td>
                                    <td className="p-3 text-center min-w-[80px]">
                                        {q.total_attempts > 0 ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <span className={`text-sm font-bold ${successColor(q.success_rate)}`}>{q.success_rate}%</span>
                                                <div className="w-12 bg-gray-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                                                    <div className={`${successBarColor(q.success_rate)} h-1.5 rounded-full`} style={{ width: `${q.success_rate}%` }} />
                                                </div>
                                            </div>
                                        ) : <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>}
                                    </td>
                                    <td className="p-3 text-center text-gray-500 text-xs whitespace-nowrap">
                                        {q.avg_time !== null ? `${q.avg_time}s` : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                    </td>
                                    <td className="p-3 text-center">
                                        {q.report_count > 0
                                            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">{q.report_count}</span>
                                            : <span className="text-gray-200 dark:text-gray-700">—</span>}
                                    </td>
                                    <td className="p-3 text-center">
                                        <button onClick={() => toggle(q.id)} disabled={loadingId === q.id}
                                            className="text-xs px-2 py-0.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-40">
                                            {loadingId === q.id ? '…' : expandedId === q.id ? '▲' : '▼'}
                                        </button>
                                    </td>
                                </tr>
                                {expandedId === q.id && (
                                    <tr className="bg-fuchsia-50 dark:bg-fuchsia-900/10">
                                        <td colSpan={7} className="px-5 pb-4 pt-3">
                                            <div className="grid md:grid-cols-2 gap-5">
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Distribution</p>
                                                    <RatingBars hard={q.hard_count} medium={q.medium_count} easy={q.easy_count} total={q.total_attempts} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Mauvaises réponses fréquentes</p>
                                                    {details[q.id]?.wrong_answers?.length > 0 ? (
                                                        <div className="space-y-1.5">
                                                            {details[q.id].wrong_answers.map((wa, i) => {
                                                                const pct = Math.round((wa.count / details[q.id].total_wrong) * 100);
                                                                return (
                                                                    <div key={i} className="flex items-center gap-2 text-xs">
                                                                        <span className="flex-1 text-gray-700 dark:text-gray-300 truncate font-medium" title={wa.answer}>{wa.answer}</span>
                                                                        <div className="w-16 bg-gray-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                                                                            <div className="bg-red-400 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                                                        </div>
                                                                        <span className="w-12 text-right text-gray-500">{wa.count}×</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-400">
                                                            {['matching', 'ordering', 'fill_in_the_blank'].includes(q.type)
                                                                ? 'Détail non disponible pour ce type.'
                                                                : 'Aucune mauvaise réponse enregistrée.'}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── FlashcardTab ──────────────────────────────────────────────────────────────
function FlashcardTab({ data }) {
    const [search, setSearch]         = useState('');
    const [sortBy, setSortBy]         = useState('fail_rate');
    const [expandedId, setExpandedId] = useState(null);

    const withAttempts = data.filter(f => f.total_attempts > 0);
    const avgSuccess   = withAttempts.length
        ? Math.round(withAttempts.reduce((s, f) => s + f.success_rate, 0) / withAttempts.length)
        : null;

    const filtered = data
        .filter(f => f.question.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
            if (sortBy === 'fail_rate') {
                const fa = a.total_attempts ? a.hard_count / a.total_attempts : -1;
                const fb = b.total_attempts ? b.hard_count / b.total_attempts : -1;
                return fb - fa;
            }
            if (sortBy === 'attempts') return b.total_attempts - a.total_attempts;
            return a.question.localeCompare(b.question);
        });

    if (data.length === 0) {
        return <p className="text-center py-10 text-gray-400 text-sm">Aucune flashcard pour cette notion.</p>;
    }

    return (
        <div className="space-y-4">
            {avgSuccess !== null && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Taux de réussite moyen :
                    <span className={`ml-1 font-bold ${successColor(avgSuccess)}`}>{avgSuccess}%</span>
                </p>
            )}
            <div className="flex flex-wrap gap-3 items-center">
                <input
                    value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher une flashcard…"
                    className="flex-1 min-w-[180px] px-3 py-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                />
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none">
                    <option value="fail_rate">Taux d'échec</option>
                    <option value="attempts">Révisions</option>
                    <option value="alpha">Alphabétique</option>
                </select>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[460px]">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                        <tr>
                            <th className="p-3 text-left">Question</th>
                            <th className="p-3 text-center">Révisions</th>
                            <th className="p-3 text-center">Réussite</th>
                            <th className="p-3 text-center">Élèves</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                        {filtered.length === 0 && (
                            <tr><td colSpan={5} className="p-6 text-center text-gray-400">Aucun résultat.</td></tr>
                        )}
                        {filtered.map(f => (
                            <React.Fragment key={f.id}>
                                <tr className={`hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${expandedId === f.id ? 'bg-fuchsia-50 dark:bg-fuchsia-900/10' : ''}`}>
                                    <td className="p-3 max-w-[260px]">
                                        <p className="font-medium text-gray-800 dark:text-gray-200 truncate" title={f.question}>{f.question}</p>
                                    </td>
                                    <td className="p-3 text-center font-medium text-gray-700 dark:text-gray-300">
                                        {f.total_attempts || <span className="text-gray-300 dark:text-gray-600">0</span>}
                                    </td>
                                    <td className="p-3 text-center min-w-[80px]">
                                        {f.total_attempts > 0 ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <span className={`text-sm font-bold ${successColor(f.success_rate)}`}>{f.success_rate}%</span>
                                                <div className="w-12 bg-gray-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                                                    <div className={`${successBarColor(f.success_rate)} h-1.5 rounded-full`} style={{ width: `${f.success_rate}%` }} />
                                                </div>
                                            </div>
                                        ) : <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>}
                                    </td>
                                    <td className="p-3 text-center text-gray-600 dark:text-gray-400">
                                        {f.unique_students || <span className="text-gray-300 dark:text-gray-600">0</span>}
                                    </td>
                                    <td className="p-3 text-center">
                                        <button onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                                            className="text-xs px-2 py-0.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                                            {expandedId === f.id ? '▲' : '▼'}
                                        </button>
                                    </td>
                                </tr>
                                {expandedId === f.id && (
                                    <tr className="bg-fuchsia-50 dark:bg-fuchsia-900/10">
                                        <td colSpan={5} className="px-5 pb-4 pt-3">
                                            <div className="grid md:grid-cols-2 gap-5">
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Distribution</p>
                                                    <RatingBars hard={f.hard_count} medium={f.medium_count} easy={f.easy_count} total={f.total_attempts} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Réponse attendue</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-black/20 rounded-xl px-3 py-2 border border-gray-100 dark:border-white/10">{f.answer}</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── ReportsTab ────────────────────────────────────────────────────────────────
function ReportsTab({ data, onResolve }) {
    if (data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-14 text-gray-400 gap-3">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">Aucun signalement en attente</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {data.map(r => (
                <div key={r.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-red-200 dark:border-red-500/20 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-400 mb-1">
                                {new Date(r.review_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate" title={r.quiz_question}>{r.quiz_question}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{r.student_name} — {r.student_email}</p>
                        </div>
                        <button onClick={() => onResolve(r.id)}
                            className="shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold border border-emerald-300 dark:border-emerald-600 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                            ✓ Résoudre
                        </button>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3">
                        <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">Signalement :</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{r.report}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── NotionStatsModal (export) ─────────────────────────────────────────────────
export default function NotionStatsModal({ notionId, notionName, onClose }) {
    const [quizData, setQuizData]       = useState(null);
    const [fcData, setFcData]           = useState(null);
    const [reportsData, setReportsData] = useState(null);
    const [loading, setLoading]         = useState(true);
    const [tab, setTab]                 = useState('quiz');

    useEffect(() => {
        const load = async () => {
            try {
                const [qRes, fRes, rRes] = await Promise.all([
                    apiClient(`${BASE}/admin_stats.php?action=quiz&notion_id=${notionId}`),
                    apiClient(`${BASE}/admin_stats.php?action=flashcard&notion_id=${notionId}`),
                    apiClient(`${BASE}/admin_stats.php?action=reports&notion_id=${notionId}`),
                ]);
                const [q, f, r] = await Promise.all([qRes.json(), fRes.json(), rRes.json()]);
                if (q.success) setQuizData(q.data);
                if (f.success) setFcData(f.data);
                if (r.success) setReportsData(r.data);
            } catch {}
            setLoading(false);
        };
        load();
    }, [notionId]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleResolve = async (id) => {
        try {
            const r = await (await apiClient(`${BASE}/admin_stats.php?action=resolve_report`, {
                method: 'POST',
                body: JSON.stringify({ id }),
            })).json();
            if (r.success) setReportsData(d => d.filter(rep => rep.id !== id));
        } catch {}
    };

    const reportCount = reportsData?.length ?? 0;

    const TABS = [
        { key: 'quiz',      label: 'Quiz',        count: quizData?.length ?? '…',      alert: false },
        { key: 'flashcard', label: 'Flashcards',   count: fcData?.length ?? '…',        alert: false },
        { key: 'reports',   label: 'Signalements', count: reportCount,                  alert: reportCount > 0 },
    ];

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/10 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">◆ {notionName}</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Statistiques de révision</p>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 px-6 border-b border-gray-100 dark:border-white/10 shrink-0">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-2 ${
                                tab === t.key
                                    ? 'text-fuchsia-600 dark:text-fuchsia-400 border-b-2 border-fuchsia-500 -mb-px'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}>
                            {t.label}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                t.alert ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300'
                            }`}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                            <div className="w-5 h-5 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm">Chargement…</span>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {/* Difficulty bar — always visible */}
                            <DifficultyBar quizzes={quizData || []} flashcards={fcData || []} />

                            {/* Tab content */}
                            {tab === 'quiz'      && <QuizTab data={quizData || []} />}
                            {tab === 'flashcard' && <FlashcardTab data={fcData || []} />}
                            {tab === 'reports'   && <ReportsTab data={reportsData || []} onResolve={handleResolve} />}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Utilitaire exporté pour la mini-barre dans les boutons
export function calcDifficultyRatio(quizStats) {
    const hard   = quizStats.reduce((s, q) => s + q.hard_count, 0);
    const medium = quizStats.reduce((s, q) => s + q.medium_count, 0);
    const easy   = quizStats.reduce((s, q) => s + q.easy_count, 0);
    const total  = hard + medium + easy;
    return total ? { hard, medium, easy, total } : null;
}
